/**
 * ProfileStats
 *
 * Interactive stats display for profile header.
 * Shows TasteBuds (friends), Followers, and Following counts
 * with haptic feedback.
 */
import { Box, HStack, VStack, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { haptics } from '@/utils/haptics'
import { FavoriteCuisine, getDominantCuisineColor } from '@/utils/cuisineColorMapping'

const MotionBox = motion.create(Box)

interface ProfileStatsProps {
  stats: {
    friendsCount: number
    followersCount: number
    followingCount?: number
  }
  onStatsClick?: {
    onFriendsClick?: () => void
    onFollowersClick?: () => void
    onFollowingClick?: () => void
  }
  /** User's favorite cuisines for accent color */
  cuisines?: FavoriteCuisine[]
}

export function ProfileStats({
  stats,
  onStatsClick,
  cuisines = [],
}: ProfileStatsProps) {
  const accentColor = getDominantCuisineColor(cuisines)

  const StatItem = ({
    count,
    label,
    onClick,
  }: {
    count: number
    label: string
    onClick?: () => void
  }) => {
    const handleClick = async () => {
      if (onClick) {
        // Trigger haptic feedback on native
        await haptics.spring()
        onClick()
      }
    }

    // D66: this component renders on two different surfaces depending on
    // whether a click handler is supplied. Interactive stats get a
    // `--card-surface-2` chip (a real card-family nested surface — light
    // mode's #F5F5F7), and the original `white`/`whiteAlpha.800` text was
    // near-invisible on it (~1.07:1, confirmed by the D66 sweep on
    // /profile). Non-interactive stats render straight on ProfileHeader's
    // TasteAuraBackground gradient hero, a photo/gradient-overlay surface
    // (wiki §7 "Deliberate exceptions") where the original white text is
    // correct and unchanged.
    const isChip = Boolean(onClick)

    const content = (
      <VStack spacing={1} minWidth="70px">
        {/* Count */}
        <Text
          fontWeight="bold"
          fontSize={{ base: 'xl', md: '2xl' }}
          color={isChip ? 'var(--card-text)' : 'white'}
          textShadow={isChip ? undefined : '0 1px 2px rgba(0,0,0,0.2)'}
          lineHeight="1.2"
        >
          {formatCount(count)}
        </Text>

        {/* Label */}
        <Text
          fontSize={{ base: 'xs', md: 'sm' }}
          color={isChip ? 'var(--card-text-2)' : 'whiteAlpha.800'}
          fontWeight="medium"
          textTransform="uppercase"
          letterSpacing="0.5px"
        >
          {label}
        </Text>
      </VStack>
    )

    if (onClick) {
      return (
        <MotionBox
          as="button"
          onClick={handleClick}
          cursor="pointer"
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          px={3}
          py={2}
          borderRadius="var(--r-chip)"
          bg="var(--card-surface-2)"
          _hover={{ bg: 'var(--card-divider)' }}
        >
          {content}
        </MotionBox>
      )
    }

    return (
      <Box px={3} py={2}>
        {content}
      </Box>
    )
  }

  return (
    <HStack
      spacing={{ base: 2, md: 4 }}
      pt={3}
      justify={{ base: 'center', md: 'flex-start' }}
      flexWrap="wrap"
    >
      <StatItem
        count={stats.friendsCount}
        label="TasteBuds"
        onClick={onStatsClick?.onFriendsClick}
      />

      <Box height="40px" width="1px" bg="whiteAlpha.300" display={{ base: 'none', md: 'block' }} />

      <StatItem
        count={stats.followersCount}
        label="Followers"
        onClick={onStatsClick?.onFollowersClick}
      />

      <Box height="40px" width="1px" bg="whiteAlpha.300" display={{ base: 'none', md: 'block' }} />

      <StatItem
        count={stats.followingCount || 0}
        label="Following"
        onClick={onStatsClick?.onFollowingClick}
      />
    </HStack>
  )
}

/**
 * Format count for display (1234 -> "1.2K", etc.)
 */
function formatCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`
  }
  return count.toString()
}

/**
 * Compact version for smaller displays or list items
 */
export function ProfileStatsCompact({
  stats,
  onStatsClick,
}: Pick<ProfileStatsProps, 'stats' | 'onStatsClick'>) {
  const handleClick = async (callback?: () => void) => {
    if (callback) {
      await haptics.selection()
      callback()
    }
  }

  return (
    <HStack spacing={4} fontSize="sm">
      <Text
        as="button"
        onClick={() => handleClick(onStatsClick?.onFriendsClick)}
        cursor={onStatsClick?.onFriendsClick ? 'pointer' : 'default'}
        _hover={onStatsClick?.onFriendsClick ? { opacity: 0.7 } : undefined}
      >
        <Text as="span" fontWeight="bold">
          {formatCount(stats.friendsCount)}
        </Text>{' '}
        <Text as="span" color="var(--card-text-2)">
          TasteBuds
        </Text>
      </Text>

      <Text
        as="button"
        onClick={() => handleClick(onStatsClick?.onFollowersClick)}
        cursor={onStatsClick?.onFollowersClick ? 'pointer' : 'default'}
        _hover={onStatsClick?.onFollowersClick ? { opacity: 0.7 } : undefined}
      >
        <Text as="span" fontWeight="bold">
          {formatCount(stats.followersCount)}
        </Text>{' '}
        <Text as="span" color="var(--card-text-2)">
          Followers
        </Text>
      </Text>
    </HStack>
  )
}
